<template>
	<div
		ref="wrapper"
		class="layout"
		:class="{
			'is-mobile': isMobileViewport,
		}"
	>
		<slot />
		<div
			class="layout__inner"
		>
			<div
				v-if="!isNarrowAnalysis"
				class="layout__actions -left"
			>
				<slot name="actions-left" />
			</div>
			<div class="layout__media">
				<slot name="media" />
			</div>
			<div
				v-show="isSidebarOpen"
				class="layout__actions -right"
			>
				<slot name="actions" />
			</div>
		</div>

		<transition name="layout__settings">
			<SettingsPageDesktop
				v-if="!isMobileViewport"
				v-show="isTaggingToolSettingsOpen"
				:show-tag-group-create-page="showTagGroupCreatePage"
			/>
		</transition>
	</div>
</template>

<script>
import toggleFullscreen from '../../../util/toggleFullscreen';
import { EVENT, bus } from '@videotools/js/events';
import SettingsPageDesktop from '@videotools/js/analysis/pages/SettingsPageDesktop';
import { mapGetters, mapActions } from 'vuex';

const events = [EVENT.FULLSCREEN_TOGGLE];

export default {
	name:       'LayoutTaggingTool',
	components: { SettingsPageDesktop },
	data() {
		return {
			settingsActive:             false,
			settingsOpenTagGroupCreate: false,
			mediaResizeObserver:        null,
			mediaObserver:              null,
		};
	},
	computed: {
		...mapGetters({
			isMobileAppleDevice:       'device/isMobileAppleDevice',
			isMobileViewport:          'device/isMobileViewport',
			isNarrowAnalysis:          'device/isNarrowAnalysis',
			isTaggingToolSettingsOpen: 'app/isTaggingToolSettingsOpen',
			showTagGroupCreatePage:    'app/showTagGroupCreatePage',
			isSidebarOpen:             'app/isSidebarOpen',
		}),
	},
	watch: {
		isMobileViewport() {
			this.setTaggingToolSettingsOpen(false);
			this.updatePlayerMaxHeight();
		},
	},
	mounted() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}

		// Set up observer for when .js-tagging-tool-media appears in the DOM
		this.setupMediaObserver();

		// Initialize the player height
		this.updatePlayerMaxHeight();

		// Add window resize listener
		window.addEventListener('resize', this.updatePlayerMaxHeight);
	},
	destroyed() {
		// bus
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}

		// Clean up observers and event listeners
		if (this.mediaResizeObserver) {
			this.mediaResizeObserver.disconnect();
		}

		if (this.mediaObserver) {
			this.mediaObserver.disconnect();
		}

		window.removeEventListener('resize', this.updatePlayerMaxHeight);
	},
	methods: {
		...mapActions({
			setTaggingToolSettingsOpen: 'app/setTaggingToolSettingsOpen',
		}),
		[`BUS-${EVENT.FULLSCREEN_TOGGLE}`]() {
			if (this.isMobileAppleDevice) return;
			toggleFullscreen(this.$root.$el);
		},
		setupMediaObserver() {
			// Observer to detect when .js-tagging-tool-media appears in the DOM
			this.mediaObserver = new MutationObserver(() => {
				const mediaElement = document.querySelector('.js-tagging-tool-media');
				if (mediaElement) {
					this.setupMediaResizeObserver(mediaElement);
					this.updatePlayerMaxHeight();
					this.mediaObserver.disconnect();
				}
			});

			this.mediaObserver.observe(document.body, {
				childList: true,
				subtree:   true,
			});
		},
		setupMediaResizeObserver(mediaElement) {
			// ResizeObserver to monitor width changes of .js-tagging-tool-media
			this.mediaResizeObserver = new ResizeObserver(() => {
				this.updatePlayerMaxHeight();
			});

			this.mediaResizeObserver.observe(mediaElement);
		},
		updatePlayerMaxHeight() {
			const playerElement = document.querySelector('.js-player');
			if (!playerElement) return;

			const windowHeight = window.innerHeight;
			const controlsElement = document.querySelector('.js-one-tagging-tool-controls');
			const headerElement = document.querySelector('.js-one-header');

			const controlsHeight = controlsElement ? controlsElement.offsetHeight : 0;
			const headerHeight = headerElement ? headerElement.offsetHeight : 0;

			const maxHeight = windowHeight - controlsHeight - headerHeight;

			playerElement.style.maxHeight = `${maxHeight}px`;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.layout {
	position: relative;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.layout__inner {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	overflow: hidden;

	.layout:not(.is-mobile) & {
		align-items: stretch;
		flex-direction: row;
		flex-wrap: nowrap;
		gap: $sp2;
	}
}

.layout__media {
	display: flex;

	.layout:not(.is-mobile) & {
		flex-grow: 1;
		flex-shrink: 1;
	}
}

.layout__player-container {
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	height: auto;
	position: relative;
}

.layout__actions {
	display: flex;
	flex-grow: 1;
	width: 100%;
	overflow: hidden;

	.layout:not(.is-mobile) & {
		flex-grow: 0;
		flex-shrink: 0;
		width: 475px;
		height: auto;
	}
}

// .layout__settings {}

.layout__settings-enter-active,
.layout__settings-leave-active {
	transition: opacity $trans-time-fast, transform $trans-time-fast;
}

.layout__settings-enter,
.layout__settings-leave-to {
	opacity: 0;
	transform: scale(0.8);
}
</style>
